import { ElementRef, Injectable } from '@angular/core';
import { catchError } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { LMGlobalPersonalizationService } from '@states/services';
import * as _ from 'lodash';

@Injectable()
export class LMBannerService {
  constructor(
    private http: HttpClient,
    private lmGlobalPersonalizationService: LMGlobalPersonalizationService
  ) {}

  public lastOpenedMenuId: string;
  public lastOpenedMenu: ElementRef;

  private bannerMenuSpec: Array<any> = [];
  private expandedMenus: Array<string> = [];

  // Cache the entire menu structure
  private allLevelsResponse: LMBannerSpec;

  public saveAllLevelsResponse(response: LMBannerSpec): void {
    this.allLevelsResponse = _.cloneDeep(response)
  }

  public getAllLevelsResponse(): LMBannerSpec {
    return this.allLevelsResponse;
  }

  // TODO: implement
  public loadAllLevels() {
    return true;
  }

  /**
   * Keep track of expanded menus for pagestate
   * @param menuItem: LMMenuItemSpec
   */
  public saveExpandedMenus(menuItem: LMMenuItemSpec): void {
    this.updateBannerMenuState(this.getBannerMenuSpec(), menuItem);

    this.expandedMenus = [];
    this.saveBannerMenuState(this.getBannerMenuSpec());

    const globalPersonalization = this.lmGlobalPersonalizationService.getGlobalPersonalization();
    globalPersonalization.expandedMenus = this.expandedMenus;
    this.lmGlobalPersonalizationService.saveGlobalPersonalization(globalPersonalization);
  }

  /**
   * Used to clear when roles change
   */
  public clearExpandedMenus() {
    this.expandedMenus = [];

    const globalPersonalization = this.lmGlobalPersonalizationService.getGlobalPersonalization();
    globalPersonalization.expandedMenus = this.expandedMenus;
    this.lmGlobalPersonalizationService.saveGlobalPersonalization(globalPersonalization);
  }

  public getExpandedMenus(): Array<string> {
    return this.lmGlobalPersonalizationService.getGlobalPersonalization().expandedMenus;
  }

  public saveLastMenuItemClicked(menuIndex: string) {
    const globalPersonalization = this.lmGlobalPersonalizationService.getGlobalPersonalization();
    globalPersonalization.lastClickedMenuItem = menuIndex;
    this.lmGlobalPersonalizationService.saveGlobalPersonalization(globalPersonalization);
  }

  public getLastMenuItemClicked() {
    return this.lmGlobalPersonalizationService.getGlobalPersonalization().lastClickedMenuItem;
  }

  public updateBannerMenuSpec(bannerMenuSpec: Array<any>, key?: string, menu?: Array<any>): void {
    if (this.bannerMenuSpec.length === 0) {
      this.bannerMenuSpec = bannerMenuSpec;
    } else {
      const newMenu: Array<any> = bannerMenuSpec;
      this._updateBannerMenuSpec(this.bannerMenuSpec, newMenu, key);
    }
  }

  public getBannerMenuSpec(): Array<any> {
    return this.bannerMenuSpec;
  }

  public setBannerMenuSpec(newMenuSpec: Array<any>) {
    this.bannerMenuSpec = newMenuSpec;
  }

  /**
   * Keep track of menu expanded/selected state
   * Later saved to state so accordions can be manually initialized and toggled
   * @param menuSpec
   * @param menuItem
   */
  private updateBannerMenuState(menuSpec, menuItem) {
    menuSpec.forEach((ms) => {
      if (menuItem.parentIds && menuItem.parentIds.includes(ms.menuIndex)) {
        ms.childExpanded = true;
        menuItem.parentExpanded = true;
      }

      // Reset menu if parent and child is not expanded
      if ((menuItem.menuIndex !== ms.menuIndex && ms.expanded) && (!menuItem.parentExpanded && !ms.childExpanded)
      ) {
        ms.menu = ms.menu ? [] : undefined;
        ms.expanded = false;
        ms.childExpanded = false;
        ms.parentExpanded = false;
      }

      // Collapse if at the same level and not the currently expanded menuItem
      if ((ms.parentIds && menuItem.parentIds && ms.parentIds[ms.parentIds.length - 1] === menuItem.parentIds[menuItem.parentIds.length - 1])
        && ms.menuIndex !== menuItem.menuIndex
      ) {
        if (ms.menu && ms.menu.length > 0) {
          ms.menu = [];
        }
        ms.expanded = false;
        ms.childExpanded = false;
        ms.parentExpanded = false;
      }

      if (ms.menu && ms.menu.length > 0) {
        this.updateBannerMenuState(ms.menu, menuItem);
      }
    })
  }

  private saveBannerMenuState(menuSpec): void {
    menuSpec.forEach((menuSpecItem: LMMenuItemSpec) => {
      if (menuSpecItem.expanded) {
        this.expandedMenus.push(menuSpecItem.menuIndex)
      }

      if (menuSpecItem.menu && menuSpecItem.menu.length > 0) {
        this.saveBannerMenuState(menuSpecItem.menu);
      }
    })
  }

  private _updateBannerMenuSpec(menu: Array<any>, newMenu: Array<any>, key: string) {
    for (let i = 0, l = menu.length; i < l; i++) {
      const object = menu[ i ];
      for (const k in object) {
        if (object.hasOwnProperty(k)) {
          if (object[ k ] === key) {
            object.submenu = newMenu;
          } else if (object.submenu) {
            this._updateBannerMenuSpec(object.submenu, newMenu, key);
          }
        }
      }
    }
  }

  public getLastOpenedMenuId(): string {
    return this.lastOpenedMenuId;
  }

  public setLastOpenedMenuId(menu: string) {
    this.lastOpenedMenuId = menu;
  }

  public getLastOpenedMenu(): ElementRef {
    return this.lastOpenedMenu;
  }

  public setLastOpenedMenu(menu: ElementRef) {
    this.lastOpenedMenu = menu;
  }

  public resetLastOpenedMenu() {
    this.lastOpenedMenu = null;
    this.lastOpenedMenuId = '';
  }

  public getChangePasswordAction(actions, name): LMActionSpec {
    if (!name) { // safety
      return null;
    }

    for (let i = 0; i < actions.length; i++) {
      if (actions[ i ].name === name) {
        return actions[ i ];
      }
    }
    return null;
  }

  public updateActorContext(bannerUrl: string, userContextProperties: {}): Observable<any>  {
    const postContent: any = {
      requestType:    'updateActorContext',
      contextProperties: userContextProperties
    };

    return this.http.post(bannerUrl, postContent).pipe(
      catchError(error => throwError(error || 'Server error')));
  }

}
